import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { BitmapText, Container } from '@pixi/react';
import * as PIXI from 'pixi.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { BUTTON_ACCENT_COLOR_NUMS } from '../../../styles/buttonStyles';
import { BUTTON_BACKGROUND_COLOR_NUM, BUTTON_BACKGROUND_HOVER_COLOR_NUM } from '../../../styles/colors';
import { getBitmapTextDimensions } from '../../../utils/textMetricsUtils';
import { isTestEnvironment } from '../../../utils/utils';
import { Rect } from './rect';
import { RectWithHighlight } from './rectWithHighlight';
const DEFAULT_PADDING_PX = 4;
const LABEL_SPACING_PX = -12;
const ACCENT_COLOR_SIZE_PX = 5;
function getPaddingW(propsPaddingW, propsPadding) {
    if (propsPaddingW !== undefined) {
        return propsPaddingW;
    }
    if (propsPadding !== undefined) {
        return propsPadding;
    }
    return DEFAULT_PADDING_PX;
}
function getPaddingH(propsPaddingH, propsPadding) {
    if (propsPaddingH !== undefined) {
        return propsPaddingH;
    }
    if (propsPadding !== undefined) {
        return propsPadding;
    }
    return DEFAULT_PADDING_PX;
}
function getSingleLabelRenderProperties(buttonBounds, hasAccentColor, paddingW, paddingH, shrinkToFit, labelProperties) {
    const labelDimensions = getBitmapTextDimensions(labelProperties.label, labelProperties.bitmapTextStyleType, {
        maxWidth: buttonBounds.w
    });
    const buttonDimensions = shrinkToFit
        ? {
            w: Math.min(buttonBounds.w, labelDimensions.w + 8 * paddingW),
            h: Math.min(buttonBounds.h, labelDimensions.h + 8 * paddingH)
        }
        : { w: buttonBounds.w, h: buttonBounds.h };
    let scale = 1;
    const maxLabelWidth = buttonDimensions.w - paddingW * 4;
    const maxLabelHeight = buttonDimensions.h - paddingH * 4 - (hasAccentColor ? ACCENT_COLOR_SIZE_PX * 2 : 0);
    if (labelDimensions.w > maxLabelWidth || labelDimensions.h > maxLabelHeight) {
        // scale down
        const scaleFittingWidth = Math.max(0, maxLabelWidth / labelDimensions.w);
        const scaleFittingHeight = Math.max(0, maxLabelHeight / labelDimensions.h);
        scale = Math.min(scaleFittingWidth, scaleFittingHeight);
    }
    const labelRenderProperties = {
        label: labelProperties.label,
        bitmapTextStyleType: labelProperties.bitmapTextStyleType,
        labelPixiDataType: labelProperties.labelPixiDataType,
        x: -Math.round(labelDimensions.w / 2),
        y: -Math.round(labelDimensions.h / 2) - (hasAccentColor ? ACCENT_COLOR_SIZE_PX : 0),
        maxWidth: buttonBounds.w
    };
    return { allLabelRenderProperties: [labelRenderProperties], buttonDimensions, scale };
}
function getMultiLabelRenderProperties(buttonBounds, hasAccentColor, paddingW, paddingH, labelProperties, subLabelProperties) {
    const labelDimensions = getBitmapTextDimensions(labelProperties.label, labelProperties.bitmapTextStyleType);
    const subLabelDimensions = getBitmapTextDimensions(subLabelProperties.label, subLabelProperties.bitmapTextStyleType);
    const combinedLabelDimensions = {
        w: Math.max(labelDimensions.w, subLabelDimensions.w),
        h: labelDimensions.h + subLabelDimensions.h + LABEL_SPACING_PX
    };
    const maxLabelWidth = buttonBounds.w - paddingW * 4;
    const maxLabelHeight = buttonBounds.h - paddingH * 4 - (hasAccentColor ? ACCENT_COLOR_SIZE_PX * 2 : 0);
    let scale = 1;
    if (combinedLabelDimensions.w > maxLabelWidth || combinedLabelDimensions.h > maxLabelHeight) {
        // scale down
        const scaleFittingWidth = Math.max(0, maxLabelWidth / combinedLabelDimensions.w);
        const scaleFittingHeight = Math.max(0, maxLabelHeight / combinedLabelDimensions.h);
        scale = Math.min(scaleFittingWidth, scaleFittingHeight);
    }
    const labelRenderProperties = {
        label: labelProperties.label,
        bitmapTextStyleType: labelProperties.bitmapTextStyleType,
        labelPixiDataType: labelProperties.labelPixiDataType,
        x: Math.round(-labelDimensions.w / 2),
        y: Math.round(-labelDimensions.h / 2 - subLabelDimensions.h / 2 - LABEL_SPACING_PX / 2) -
            (hasAccentColor ? ACCENT_COLOR_SIZE_PX : 0)
    };
    const subLabelRenderProperties = {
        label: subLabelProperties.label,
        bitmapTextStyleType: subLabelProperties.bitmapTextStyleType,
        labelPixiDataType: subLabelProperties.labelPixiDataType,
        x: Math.round(-subLabelDimensions.w / 2),
        y: Math.round(labelRenderProperties.y + labelDimensions.h + LABEL_SPACING_PX / 2)
    };
    return {
        allLabelRenderProperties: [labelRenderProperties, subLabelRenderProperties],
        buttonDimensions: { w: buttonBounds.w, h: buttonBounds.h },
        scale
    };
}
/**
 * Given the properties for a primary button label and an optional sub-label, figure out the overall dimensions
 * of the button and where (relative to button center) each label should be rendered.
 */
function getAllLabelsRenderProperties(buttonBounds, hasAccentColor, paddingW, paddingH, shrinkToFit, labelProperties, subLabelProperties) {
    if (!subLabelProperties) {
        return getSingleLabelRenderProperties(buttonBounds, hasAccentColor, paddingW, paddingH, shrinkToFit, labelProperties);
    }
    return getMultiLabelRenderProperties(buttonBounds, hasAccentColor, paddingW, paddingH, labelProperties, subLabelProperties);
}
function maybeRenderAccentColor(accentColor, rectX, rectY, rectW, rectH) {
    if (!accentColor) {
        return null;
    }
    const w = Math.max(rectW - 14, ACCENT_COLOR_SIZE_PX);
    const x = rectX + (rectW - w) / 2;
    const y = rectY + rectH - 2 * ACCENT_COLOR_SIZE_PX;
    const h = ACCENT_COLOR_SIZE_PX;
    const fillColor = BUTTON_ACCENT_COLOR_NUMS[accentColor];
    const radius = Math.floor(ACCENT_COLOR_SIZE_PX / 2);
    return _jsx(Rect, { x: x, y: y, w: w, h: h, fillColor: fillColor, radius: radius });
}
export const Button = ({ bounds: buttonBounds, labelProperties, subLabelProperties, onClick, pixiObjectData, lineWidth, lineColor, highlightRatio, highlightPaddingPx, accentColor, padding: propsPadding, paddingW: propsPaddingW, paddingH: propsPaddingH, radius = 12, shrinkToFit, backgroundColor = BUTTON_BACKGROUND_COLOR_NUM, backgroundHoverColor = BUTTON_BACKGROUND_HOVER_COLOR_NUM }) => {
    const isPressedRef = useRef(false);
    const [fillColor, setFillColor] = useState(backgroundColor);
    const paddingW = getPaddingW(propsPaddingW, propsPadding);
    const paddingH = getPaddingH(propsPaddingH, propsPadding);
    const onMouseOver = useCallback(() => {
        if (isMobile) {
            return;
        }
        setFillColor(backgroundHoverColor);
    }, [backgroundHoverColor]);
    const onMouseOut = useCallback(() => {
        if (isMobile) {
            return;
        }
        setFillColor(backgroundColor);
    }, [backgroundColor]);
    const onPointerDown = useCallback((event) => {
        event.stopPropagation();
        event.preventDefault();
        setFillColor(backgroundHoverColor);
        isPressedRef.current = true;
    }, [backgroundHoverColor]);
    const onPointerUpOutside = useCallback(() => {
        isPressedRef.current = false;
        setFillColor(backgroundColor);
    }, [backgroundColor]);
    const onPointerUp = useCallback((event) => {
        event.stopPropagation();
        event.preventDefault();
        if (isPressedRef.current && event.button === 0) {
            // only respond to left clicks
            onClick();
        }
        isPressedRef.current = false;
        setFillColor(backgroundColor);
    }, [backgroundColor, onClick]);
    // Necessary to see a change when clicking on the button that results in its background color changing (while still hovering over it):
    useEffect(() => {
        setFillColor(backgroundColor);
    }, [backgroundColor]);
    const { allLabelRenderProperties, buttonDimensions, scale } = useMemo(() => getAllLabelsRenderProperties(buttonBounds, !!accentColor, paddingW, paddingH, shrinkToFit, labelProperties, subLabelProperties), [buttonBounds, accentColor, paddingW, paddingH, shrinkToFit, labelProperties, subLabelProperties]);
    const hitArea = useMemo(() => new PIXI.Rectangle(-buttonDimensions.w / 2, -buttonDimensions.h / 2, buttonDimensions.w, buttonDimensions.h), [buttonDimensions.h, buttonDimensions.w]);
    const rectX = Math.floor(-hitArea.width / 2) + paddingW;
    const rectY = Math.floor(-hitArea.height / 2) + paddingH;
    const rectW = hitArea.width - paddingW * 2;
    const rectH = hitArea.height - paddingH * 2;
    return (_jsxs(Container, { x: Math.round(buttonBounds.x), y: Math.round(buttonBounds.y), cursor: 'pointer', interactive: true, 
        // implement a "click" style event on mobile by implementing pointerdown, pointerup and pointerupoutside:
        pointerdown: onPointerDown, pointerup: onPointerUp, pointerupoutside: onPointerUpOutside, accessible: true, mouseover: onMouseOver, mouseout: onMouseOut, "pixi-data": isTestEnvironment() ? pixiObjectData : null, hitArea: hitArea, children: [_jsx(RectWithHighlight, { fillColor: fillColor, x: rectX, y: rectY, w: rectW, h: rectH, radius: radius, lineWidth: lineWidth, lineColor: lineColor, highlightRatio: highlightRatio, highlightPaddingPx: highlightPaddingPx }), maybeRenderAccentColor(accentColor, rectX, rectY, rectW, rectH), _jsx(Container, { scale: scale, children: allLabelRenderProperties.map(({ label, bitmapTextStyleType, labelPixiDataType, x, y, maxWidth }) => (_jsx(BitmapText
                // Explicit key from changing properties is needed to force re-render when style changes (apparent bug in react-pixi-fiber):
                , { style: { fontName: bitmapTextStyleType, maxWidth }, text: label, x: x, y: y, "pixi-data": isTestEnvironment() && labelPixiDataType ? { type: labelPixiDataType } : null }, `${label} ${bitmapTextStyleType} ${maxWidth}`))) })] }));
};
